import React, { useCallback, useEffect, useRef, useState } from "react";

import moment from "moment";
import { FormattedMessage } from "react-intl";

import useDecodedParams from "Hooks/useDecodedParams";
import { getAutoscalingConfigurationThunk } from "Reducers/environment/service/service.slice";
import { projectCapabilitySelector } from "Reducers/project/capabilities";
import ActivityLogModal from "src/organization/pages/project/environment/metrics/components/ActivityLogModal/ActivityLogModal";
import TimeframeControl from "src/organization/pages/project/environment/metrics/components/timeframeControl/TimeframeControl";
import { useAutoscallingValues } from "src/organization/pages/project/environment/metrics/containers/Service/util";
import useInitialTimeframe from "src/organization/pages/project/environment/metrics/hooks/useInitialTimeframe";
import usePinnedTabsSync from "src/organization/pages/project/environment/metrics/hooks/usePinnedTabsSync";
import usePollActivities from "src/organization/pages/project/environment/metrics/hooks/usePollActivities";
import useStickyTimecontrol from "src/organization/pages/project/environment/metrics/hooks/useStickyTimecontrol";
import { defaultPollTime } from "src/organization/pages/project/environment/metrics/resources/settings";
import * as V from "src/organization/pages/project/environment/metrics/resources/views.styles";
import { parseTimeDuration } from "src/organization/pages/project/environment/metrics/utils/parseDimensions/parseDimensions";
import usePollgroup, {
  PollgroupContext
} from "src/organization/pages/project/environment/metrics/utils/polling/usePollgroup";
import { useAppDispatch, useAppSelector } from "Store/hooks";

import UsageView from "../totalUsage/Page2";
import { TimeFrameType } from "../types";

import * as S from "./Grid.styles";
import ServiceGroup from "./ServiceGroup";
import { useInternalStorageServices } from "./util";

import type { Activity } from "@packages/client";

const Page = () => {
  const [maxZoom, setMaxZoom] = useState(false);
  const dispatch = useAppDispatch();
  const initialTimeframe = useInitialTimeframe();
  const [timeframe, setTimeframe] = useState(initialTimeframe);
  const [deployment, setDeployment] = useState(false);
  const [lastUpdated, setLastUpdated] = useState<Date>();
  const [activities, setActivities] = useState<Activity[]>([]);
  const { organizationId, projectId, environmentId } = useDecodedParams();

  const pollInterval = parseTimeDuration(
    initialTimeframe.poll || defaultPollTime[timeframe.id || "fallback"]
  );

  const capabilites = useAppSelector(state =>
    projectCapabilitySelector(state, { projectId, organizationId })
  );
  const isAutoscalingEnabled = capabilites?.autoscaling?.enabled;
  const { hasCapability } = useAutoscallingValues();

  usePollActivities({
    enabled: deployment || hasCapability,
    timeframe,
    onChange: setActivities,
    interval: pollInterval.seconds
  });

  const { pollgroup } = usePollgroup({
    timeframe,
    interval: pollInterval.seconds
  });

  const onTimeframeChange = useCallback((timeframe: TimeFrameType) => {
    setTimeframe(timeframe);
    setMaxZoom(false);
  }, []);

  const onBrush = useCallback(
    (from: Date | string, to: Date | string) => {
      if (maxZoom) {
        setTimeframe(timeframe);
        return;
      }
      const selectedRange = moment(to).diff(moment(from), "minutes");
      if (selectedRange < 3) {
        const newEndpoint = moment(from).add(3, "minutes").format();
        setMaxZoom(true);
        setTimeframe({ from, to: newEndpoint });
      } else {
        setTimeframe({ from, to });
      }
    },
    [maxZoom, setMaxZoom, timeframe]
  );

  useEffect(() => {
    dispatch(getAutoscalingConfigurationThunk({ projectId, environmentId }));
  }, [dispatch, projectId, environmentId]);

  const headerRef = useRef<HTMLDivElement>(null);
  useStickyTimecontrol({ ref: headerRef });
  const { onSyncChange, isSynced, canJoinSync } = usePinnedTabsSync({
    setTimeframe: onTimeframeChange,
    timeframe
  });

  const handleSyncChange = (isSynced: boolean) => {
    onSyncChange(isSynced, timeframe);
  };

  const prop = {
    setLastUpdated,
    selectedTimeframe: timeframe,
    onBrush,
    deployment,
    activities: activities || [],
    maxZoom,
    interval: pollInterval
  };

  const serviceCategories = useInternalStorageServices();

  return (
    <PollgroupContext.Provider value={{ pollgroup }}>
      <ActivityLogModal />
      <V.Header ref={headerRef}>
        <V.Heading tag="h2">
          <FormattedMessage id="metrics.grid.heading" />
        </V.Heading>

        <TimeframeControl
          timeframe={{
            onChange: onTimeframeChange,
            timeframe
          }}
          pollInterval={pollInterval}
          lastUpdated={lastUpdated}
          deployment={{ enabled: deployment, onChange: setDeployment }}
          tabSync={{
            onSyncChange: handleSyncChange,
            isSynced,
            canJoinSync
          }}
        />
      </V.Header>

      <S.Layout>
        <UsageView
          setLastUpdated={setLastUpdated}
          timeframe={timeframe}
          activities={deployment || isAutoscalingEnabled ? activities : []}
          setTimeFrame={onTimeframeChange}
        />
        {serviceCategories.map(
          (services, i) =>
            !!services.length && (
              <ServiceGroup key={i} services={services} {...prop} />
            )
        )}
      </S.Layout>
    </PollgroupContext.Provider>
  );
};

export default Page;
